import api from "@/lib/api";
import { eventErrorMessage } from "@/lib/events";
import { CompetitorRead, EventRead } from "@openapi";
import { Alert, Form, Input, Modal, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export type TeamFormMode = "create" | "join";

interface TeamFormModalProps {
    /** The public user event to create or join a team in; closed when null. */
    event: EventRead | null;
    mode: TeamFormMode;
    onClose: () => void;
    /** Called with the caller's team after it was created or joined. */
    onDone: (team: CompetitorRead) => void;
}

interface TeamFormValues {
    name?: string;
    joinCode?: string;
}

// Creates a team (optional name) or joins one with a join code in a public
// user event. Server refusals are shown inline, in the student's language.
export default function TeamFormModal({
    event,
    mode,
    onClose,
    onDone,
}: TeamFormModalProps) {
    const { t } = useTranslation();
    const [form] = Form.useForm<TeamFormValues>();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const close = () => {
        form.resetFields();
        setError(null);
        onClose();
    };

    const handleFinish = async (values: TeamFormValues) => {
        if (!event) return;
        setSubmitting(true);
        setError(null);
        try {
            const team =
                mode === "create"
                    ? await api.events.createTeam({
                          eventId: event.id,
                          teamCreate: { name: values.name?.trim() || null },
                      })
                    : await api.events.joinTeam({
                          eventId: event.id,
                          teamJoin: { join_code: values.joinCode ?? "" },
                      });
            form.resetFields();
            onDone(team);
        } catch (err) {
            setError(await eventErrorMessage(err, t));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            title={
                mode === "create"
                    ? t("events.teams.createTitle", { name: event?.name ?? "" })
                    : t("events.teams.joinTitle", { name: event?.name ?? "" })
            }
            open={event !== null}
            onCancel={close}
            onOk={() => form.submit()}
            okText={
                mode === "create"
                    ? t("events.teams.create")
                    : t("events.teams.join")
            }
            cancelText={t("button.cancel")}
            confirmLoading={submitting}
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={(values) => void handleFinish(values)}
            >
                {mode === "create" ? (
                    <Form.Item
                        name="name"
                        label={t("events.teams.nameLabel")}
                        extra={t("events.teams.nameHint")}
                    >
                        <Input maxLength={100} autoFocus />
                    </Form.Item>
                ) : (
                    <Form.Item
                        name="joinCode"
                        label={t("events.teams.codeLabel")}
                        rules={[
                            {
                                required: true,
                                whitespace: true,
                                message: t("events.teams.codeRequired"),
                            },
                        ]}
                    >
                        <Input
                            autoFocus
                            autoComplete="off"
                            style={{ textTransform: "uppercase" }}
                        />
                    </Form.Item>
                )}
            </Form>
            {mode === "join" && (
                <Typography.Text type="secondary">
                    {t("events.teams.codeHint")}
                </Typography.Text>
            )}
            {error && (
                <Alert
                    type="error"
                    showIcon
                    title={error}
                    style={{ marginTop: 12 }}
                />
            )}
        </Modal>
    );
}
